import React, { useCallback, useEffect, useState } from "react";
import { Swords, Plus, Minus, Flag, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function PlayerSelect({ value, onChange, users, taken, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
      <option value="">Selecione o jogador...</option>
      {users.filter((u) => u.user_id === value || !taken.includes(u.user_id)).map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
    </select>
  );
}

function NewGameDialog({ open, onClose, users, onCreated }) {
  const [p, setP] = useState(["", "", "", ""]);
  const set = (i, v) => setP((arr) => arr.map((x, j) => (j === i ? v : x)));
  const create = async () => {
    if (p.some((x) => !x)) return toast.error("Selecione os 4 jogadores.");
    try { await api.post("/admin/games", { team_a: [p[0], p[1]], team_b: [p[2], p[3]] }); toast.success("Partida ao vivo criada!"); setP(["", "", "", ""]); onCreated(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-game-dialog" aria-describedby={undefined} className="max-w-md rounded-3xl">
        <DialogHeader><DialogTitle className="font-display text-xl">Nova partida</DialogTitle></DialogHeader>
        {[["Dupla A", 0], ["Dupla B", 2]].map(([label, off]) => (
          <div key={label} className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
            {[0, 1].map((k) => <PlayerSelect key={k} testid={`game-player-${off + k}`} value={p[off + k]} onChange={(v) => set(off + k, v)} users={users} taken={p} />)}
          </div>
        ))}
        <Button data-testid="game-create" onClick={create} className="w-full rounded-full bg-[#E8611A] text-white hover:bg-[#CF5212]">Começar jogo ao vivo</Button>
      </DialogContent>
    </Dialog>
  );
}

function FinishDialog({ game, onClose, onDone }) {
  const [winner, setWinner] = useState("");
  const [pts, setPts] = useState({});
  useEffect(() => {
    if (!game) return;
    const w = game.score_a > game.score_b ? "a" : game.score_b > game.score_a ? "b" : "";
    setWinner(w);
  }, [game]);
  useEffect(() => {
    if (!game) return;
    const init = {};
    game.team_a.forEach((pl) => { init[pl.user_id] = winner === "a" ? 3 : 0; });
    game.team_b.forEach((pl) => { init[pl.user_id] = winner === "b" ? 3 : 0; });
    setPts(init);
  }, [game, winner]);
  if (!game) return null;
  const finish = async () => {
    if (!winner) return toast.error("Selecione a dupla vencedora.");
    try { await api.post(`/admin/games/${game.id}/finish`, { score_a: game.score_a, score_b: game.score_b, winner, points: pts }); toast.success("Jogo finalizado e pontos lançados!"); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const team = (key, list) => (
    <button data-testid={`finish-winner-${key}`} onClick={() => setWinner(key)} className={`flex-1 rounded-2xl border-2 p-3 text-left transition-colors ${winner === key ? "border-[#1B7A45] bg-emerald-50" : "border-slate-200"}`}>
      <p className="text-xs font-bold uppercase text-slate-500">Dupla {key.toUpperCase()} · {key === "a" ? game.score_a : game.score_b}</p>
      <p className="text-sm font-semibold text-slate-800">{list.map((x) => x.name).join(" & ")}</p>
    </button>
  );
  return (
    <Dialog open={!!game} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="finish-game-dialog" aria-describedby={undefined} className="max-w-md rounded-3xl">
        <DialogHeader><DialogTitle className="font-display text-xl">Finalizar jogo</DialogTitle></DialogHeader>
        <p className="text-xs font-semibold text-slate-500">Vencedor</p>
        <div className="flex gap-2">{team("a", game.team_a)}{team("b", game.team_b)}</div>
        <p className="mt-2 text-xs font-semibold text-slate-500">Pontos para o ranking</p>
        <div className="space-y-2">
          {[...game.team_a, ...game.team_b].map((pl) => (
            <div key={pl.user_id} className="flex items-center justify-between gap-2">
              <span className="text-sm text-slate-700">{pl.name}</span>
              <Input data-testid={`finish-pts-${pl.user_id}`} type="number" value={pts[pl.user_id] ?? 0} onChange={(e) => setPts({ ...pts, [pl.user_id]: Number(e.target.value) })} className="w-24 rounded-xl" />
            </div>
          ))}
        </div>
        <Button data-testid="finish-confirm" onClick={finish} className="w-full rounded-full bg-[#1B7A45] text-white hover:bg-[#156337]"><Flag className="mr-1.5 h-4 w-4" /> Finalizar e lançar pontos</Button>
      </DialogContent>
    </Dialog>
  );
}

function ScoreBox({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1.5">
      <Button data-testid={`${testid}-minus`} size="sm" variant="outline" onClick={() => onChange(Math.max(0, value - 1))} className="h-9 w-9 rounded-full p-0"><Minus className="h-4 w-4" /></Button>
      <span data-testid={testid} className="w-10 text-center font-display text-3xl font-black text-slate-900">{value}</span>
      <Button data-testid={`${testid}-plus`} size="sm" onClick={() => onChange(value + 1)} className="h-9 w-9 rounded-full bg-[#1B7A45] p-0 text-white hover:bg-[#156337]"><Plus className="h-4 w-4" /></Button>
    </div>
  );
}

export default function GamesManager() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [games, setGames] = useState({ live: [], finished: [] });
  const [openNew, setOpenNew] = useState(false);
  const [finishing, setFinishing] = useState(null);

  const load = useCallback(() => api.get("/dayuse/games").then((r) => setGames(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/admin/users").then((r) => setUsers([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR")))).catch(() => {});
  }, [load]);

  const setScore = async (g, side, v) => {
    const body = { score_a: side === "a" ? v : g.score_a, score_b: side === "b" ? v : g.score_b };
    setGames((s) => ({ ...s, live: s.live.map((x) => (x.id === g.id ? { ...x, ...body } : x)) }));
    try { await api.patch(`/admin/games/${g.id}/score`, body); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };
  const del = async (id) => {
    if (!window.confirm("Excluir esta partida? Pontos já lançados não são removidos.")) return;
    try { await api.delete(`/admin/games/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const canDelete = (user?.perms || []).includes("excluir");

  return (
    <div className="mt-4" data-testid="games-manager">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2"><Swords className="h-5 w-5 text-[#E8611A]" /><h2 className="font-display text-xl font-bold tracking-tight text-slate-900">Jogos ao vivo</h2></div>
        <Button data-testid="game-add" onClick={() => setOpenNew(true)} className="rounded-full bg-[#E8611A] text-white hover:bg-[#CF5212]"><Plus className="mr-1 h-4 w-4" /> Adicionar partida</Button>
      </div>
      <div className="mt-3 space-y-3">
        {games.live.length === 0 && <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-6 text-center text-sm text-slate-400">Nenhum jogo em andamento.</p>}
        {games.live.map((g) => (
          <div key={g.id} data-testid={`live-game-${g.id}`} className="rounded-2xl border border-red-200 bg-white p-4">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-black uppercase text-red-600"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-600" /> Ao vivo</span>
            <div className="mt-2 grid grid-cols-1 items-center gap-3 sm:grid-cols-2">
              {[["a", g.team_a, g.score_a], ["b", g.team_b, g.score_b]].map(([side, team, score]) => (
                <div key={side} className="flex items-center justify-between gap-2 rounded-xl bg-slate-50 p-3">
                  <p className="min-w-0 text-sm font-semibold text-slate-800">{team.map((x) => x.name).join(" & ")}</p>
                  <ScoreBox testid={`score-${side}-${g.id}`} value={score} onChange={(v) => setScore(g, side, v)} />
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Button data-testid={`game-finish-${g.id}`} onClick={() => setFinishing(g)} size="sm" className="rounded-full bg-[#1B7A45] text-white hover:bg-[#156337]"><Flag className="mr-1 h-4 w-4" /> Finalizar</Button>
              {canDelete && <Button data-testid={`game-del-${g.id}`} onClick={() => del(g.id)} size="sm" variant="outline" className="rounded-full border-red-200 bg-red-50 text-red-600"><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </div>
        ))}
        {games.finished.length > 0 && <p className="pt-2 text-xs font-bold uppercase tracking-wider text-slate-400">Últimos resultados</p>}
        {games.finished.map((g) => (
          <div key={g.id} data-testid={`finished-game-${g.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
            <span className={g.winner === "a" ? "font-bold text-[#1B7A45]" : "text-slate-600"}>{g.team_a.map((x) => x.name).join(" & ")}</span>
            <span className="shrink-0 font-display font-black">{g.score_a} x {g.score_b}</span>
            <span className={`text-right ${g.winner === "b" ? "font-bold text-[#1B7A45]" : "text-slate-600"}`}>{g.team_b.map((x) => x.name).join(" & ")}</span>
            {canDelete && <button data-testid={`game-del-${g.id}`} onClick={() => del(g.id)} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>
      <NewGameDialog open={openNew} onClose={() => setOpenNew(false)} users={users} onCreated={load} />
      <FinishDialog game={finishing} onClose={() => setFinishing(null)} onDone={load} />
    </div>
  );
}
